// src/renderer/hooks/useRecentProjects.ts

import { useState, useEffect, useCallback } from 'react';
import { useProjectStore } from '../store/projectStore';
import { useProjectOpen } from './useProjectOpen';
import {
  getRecentProjects,
  removeFromRecentProjects,
  clearRecentProjects,
} from '../services/FileSystemService';
import type { RecentProject } from '../../core/types/project.types';

interface UseRecentProjectsReturn {
  projects: RecentProject[];
  isLoading: boolean;
  error: string | null;
  openProject: (projectPath: string) => Promise<boolean>;
  removeProject: (path: string) => Promise<void>;
  clearAll: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing recent projects list.
 * Integrates with handle storage to validate projects on load
 * and uses useProjectOpen for opening projects.
 */
export function useRecentProjects(): UseRecentProjectsReturn {
  const {
    recentProjects,
    isLoadingRecent,
    setRecentProjects,
    setIsLoadingRecent,
    clearRecentProjects: clearStore,
  } = useProjectStore();

  const [error, setError] = useState<string | null>(null);
  const { openFromRecent } = useProjectOpen();

  /**
   * Load and validate recent projects from storage.
   * Uses FileSystemService which handles validation and permission checks.
   */
  const loadProjects = useCallback(async () => {
    setIsLoadingRecent(true);
    setError(null);

    try {
      // getRecentProjects already validates handles and permissions
      const validProjects = await getRecentProjects();
      setRecentProjects(validProjects);
    } catch (err) {
      console.error('Failed to load recent projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load recent projects');
      setRecentProjects([]);
    } finally {
      setIsLoadingRecent(false);
    }
  }, [setRecentProjects, setIsLoadingRecent]);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  /**
   * Open a project from the recent list.
   * Delegates to useProjectOpen for full validation and permission handling.
   */
  const openProject = useCallback(async (path: string): Promise<boolean> => {
    return openFromRecent(path);
  }, [openFromRecent]);

  /**
   * Remove a project from the recent list.
   * Removes from both the backend storage and refreshes the local list.
   */
  const removeProject = useCallback(async (projectId: string) => {
    try {
      await removeFromRecentProjects(projectId);
      await loadProjects(); // Refresh the list
    } catch (err) {
      console.error('Failed to remove project:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove project');
    }
  }, [loadProjects]);

  /**
   * Clear all recent projects.
   */
  const clearAll = useCallback(async () => {
    try {
      await clearRecentProjects();
      clearStore();
    } catch (err) {
      console.error('Failed to clear recent projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear recent projects');
    }
  }, [clearStore]);

  /**
   * Refresh the recent projects list by reloading from storage.
   */
  const refresh = useCallback(async () => {
    await loadProjects();
  }, [loadProjects]);

  return {
    projects: recentProjects,
    isLoading: isLoadingRecent,
    error,
    openProject,
    removeProject,
    clearAll,
    refresh,
  };
}

export default useRecentProjects;
